
import { useEffect, useState } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { OfferFormValues } from '../schemas/offerFormSchema';
import { Offer } from '@/types/offer';
import { CampLocation } from '@/types/campLocation';
import { DecimalEnumMap } from '@/types/decimalEnum';

interface UseOfferFormInitializationProps {
  form: UseFormReturn<OfferFormValues>;
  initialOffer?: Offer | null;
  verifiedLocation: CampLocation;
  enumsMap: DecimalEnumMap;
  isLocationLoaded: boolean;
  resetFieldModified: (fieldName: string) => void;
}

export const useOfferFormInitialization = ({
  form,
  initialOffer,
  verifiedLocation,
  enumsMap,
  isLocationLoaded,
  resetFieldModified
}: UseOfferFormInitializationProps) => {
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize form when location data is available
  useEffect(() => {
    if (!isLocationLoaded || !verifiedLocation?.variations) {
      return;
    }

    const variationKey = form.getValues('variationKey');
    if (!variationKey) {
      return;
    }

    const variation = verifiedLocation.variations.find(v => v.key === variationKey);
    if (!variation) {
      console.warn(`[useOfferFormInitialization] Variation not found: ${variationKey}`);
      return;
    }

    form.setValue('variation', variation, {
      shouldDirty: false,
      shouldTouch: false,
      shouldValidate: false,
    });

    // If we have an initialOffer, mark fields that have been explicitly set as modified
    if (initialOffer) {
      
      // CRITICAL FIX: Mark fields as modified if they have explicit values that differ from program
      if (initialOffer.priceOverride !== null && initialOffer.priceOverride !== undefined) {
        // Only mark as modified if it differs from program value
        if (initialOffer.priceOverride !== variation.priceRegular) {
          resetFieldModified('priceOverride'); // Start fresh
        }
      }
      
      if (initialOffer.numDaysOverride !== null && initialOffer.numDaysOverride !== undefined) {
        // Check against program value (either direct or derived from enum)
        let programNumDays = variation.numDays;
        if (variation.regularOfferIncrementKey && enumsMap.has(variation.regularOfferIncrementKey)) {
          programNumDays = enumsMap.get(variation.regularOfferIncrementKey)?.value || null;
        }
        
        if (initialOffer.numDaysOverride !== programNumDays) {
          resetFieldModified('numDaysOverride'); // Start fresh
        }
      }
      
      // CRITICAL FIX: Handle footnote fields correctly
      const programPriceNote = variation.priceNote || "";
      const programTimingNote = variation.timings?.timingNote || "";
      
      // For priceNoteOverride: null = use program, anything else = user set it
      if (initialOffer.priceNoteOverride !== null) {
        if (initialOffer.priceNoteOverride !== programPriceNote) {
          resetFieldModified('priceNoteOverride'); // Start fresh
        }
      }
      
      // Respect user's existing timingNoteOverride on edit: do not mark as unmodified here
    }

    setIsInitialized(true);
  }, [form, initialOffer, verifiedLocation, enumsMap, isLocationLoaded, resetFieldModified]);

  const resetDirtyState = () => {
    form.reset(form.getValues());
  };

  return {
    isInitialized,
    resetDirtyState
  };
};
